import React, { FC, useMemo, useState } from 'react';
import clsx from 'clsx';
import { TextField, AddButton } from '@td-client-frontend/common';
import { Trash, ExclamationInTriangle } from '@td-client-frontend/icons';
import Modal from '../../../components/Modal/NewModal';
import { v4 } from 'uuid';
import { CodeOption } from '../../../components/form-controls/FieldSelectArray';
import * as _ from 'lodash';

export interface ViewPlanNumbersOption extends CodeOption {
  id: string;
  isPlainText?: boolean;
  error?: string;
}

export interface ViewPlanNumbersArgs {
  updateOptions: (codeOptions: CodeOption[]) => void;
  codeOptions: CodeOption[];
  initialCodeOptionsState: CodeOption[];
}

export interface ViewPlanNumbersState {
  viewPlanNumbersOptions: ViewPlanNumbersOption[];
  updateOptions: (codeOption: CodeOption[]) => void;
}

interface ViewPlanNumbersModal {
  isOpen: boolean;
  onClose: () => void;
  initialPlanNumbersOptions: ViewPlanNumbersOption[];
  onChange: (codeOptions: CodeOption[]) => void;
}

interface ViewPlanNumberModalBody {
  planNumbersState: ViewPlanNumbersOption[];
  onPlanNumberChange: (value: string, id: string) => void;
  onDeletePlanNumber: (id: string) => void;
  onAddPlanNumber: () => void;
}

const ViewPlanNumbersModalBody: FC<ViewPlanNumberModalBody> = ({
  planNumbersState,
  onPlanNumberChange,
  onDeletePlanNumber,
  onAddPlanNumber
}) => (
  <div className="flex flex-col w-full" data-testid="viewPlanNumbersBody">
    <ul className="px-6 py-2 max-h-96 overflow-x-auto" data-testid="viewPlanNumbersList">
      {!planNumbersState.length && (
        <li className="py-3">
          <p className="text-xl font-effra text-gray-70">Select Save button to delete all plan numbers</p>
        </li>
      )}
      {planNumbersState?.map((planNumber, index) => {
        return (
          <li
            className={clsx(
              'py-3 border-gray-20 font-effra text-lg text-gray-70',
              index + 1 === planNumbersState.length ? 'border-0' : 'border-b-1'
            )}
            key={planNumber.id}
            data-testid="viewPlanNumbersItem"
          >
            <div className="w-full flex justify-center">
              <TextField
                additionalLabelClasses="hidden"
                wrapperClasses="mb-0 w-full"
                fieldClasses="p-3 h-10"
                value={planNumber.code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPlanNumberChange(e.target.value, planNumber.id)}
                showAsPlainText={planNumber.isPlainText}
                errorClassName="hidden"
                isError={planNumber.error}
                placeholder="Type a plan number"
                data-testid="viewPlanNumberField"
              />
              <div className="w-10 h-10">
                {!planNumber.isPlainText && (
                  <button
                    className="h-10"
                    onClick={() => onDeletePlanNumber(planNumber.id)}
                    data-testid="viewPlanNumbersDeleteBtn"
                  >
                    <Trash className="fill-current text-primary-500" />
                  </button>
                )}
              </div>
            </div>
            {planNumber.error && (
              <div className="flex items-center mt-2" data-testid="viewPlanNumberError">
                <ExclamationInTriangle
                  className="fill-current text-red-500"
                  fill="#EF4444"
                  data-testid="ExclamationInTriangleIcon"
                />
                <p className="font-effra text-lg text-gray-70 ml-2">{planNumber.error}</p>
              </div>
            )}
          </li>
        );
      })}
    </ul>
    <div className="border-t-1 border-gray-20 w-full px-6">
      <AddButton label="Add" onClick={onAddPlanNumber} />
    </div>
  </div>
);

export const ViewPlanNumbersModal: FC<ViewPlanNumbersModal> = ({
  isOpen,
  onClose,
  initialPlanNumbersOptions,
  onChange
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [planNumbersState, setPlanNumbersState] = useState<ViewPlanNumbersOption[]>(initialPlanNumbersOptions);

  const isChanged: boolean = useMemo(() => {
    return (
      !planNumbersState.every(
        (planNumberState: ViewPlanNumbersOption, index: number) =>
          planNumberState?.code === initialPlanNumbersOptions[index]?.code
      ) || initialPlanNumbersOptions.length !== planNumbersState.length
    );
  }, [initialPlanNumbersOptions, planNumbersState]);

  const getError = (value: string) => {
    if (planNumbersState.some((planNumber: ViewPlanNumbersOption) => planNumber.code === value)) {
      return 'Enter a unique number';
    }
    if (_.isEmpty(value.trim())) {
      return 'Cannot be empty';
    }
    if (value.indexOf(' ') > -1) {
      return 'Cannot has white spaces';
    }
    return '';
  };

  const isAllPlanNumberValide: boolean = useMemo(() => {
    return (
      isChanged &&
      planNumbersState.every((planNumber: ViewPlanNumbersOption) => {
        return !planNumber.error && planNumber.code;
      })
    );
  }, [isChanged, planNumbersState]);

  const onPlanNumberChange = (value: string, id: string) => {
    setPlanNumbersState((prev: ViewPlanNumbersOption[]) => {
      return prev.map((planNumber: ViewPlanNumbersOption) => {
        if (planNumber.id === id) {
          return {
            ...planNumber,
            value: value,
            label: value,
            code: value,
            error: getError(value)
          };
        }
        return planNumber;
      });
    });
  };

  const onAddPlanNumber = () => {
    setPlanNumbersState((prev: ViewPlanNumbersOption[]) => {
      return [
        ...prev,
        {
          code: '',
          bpCodes: [],
          classCodes: [],
          label: '',
          value: '',
          id: v4()
        }
      ];
    });
  };

  const onDeletePlanNumber = (id: string) => {
    setPlanNumbersState((prev: ViewPlanNumbersOption[]) =>
      prev.filter((planNumber: ViewPlanNumbersOption) => planNumber.id !== id)
    );
  };

  const onSave = () => {
    if (isAllPlanNumberValide && isChanged && !showConfirmModal) {
      const preperedForSaveCodeOptions: CodeOption[] = planNumbersState.map((planNumber: ViewPlanNumbersOption) => {
        const { id, error, isPlainText, ...rest } = planNumber;
        return rest;
      });
      onChange(preperedForSaveCodeOptions);
    }
    onClose();
  };

  const onCancelViewPlanNumbersModal = () => {
    if (!showConfirmModal && isChanged) {
      setShowConfirmModal(true);
    } else if (showConfirmModal && isChanged) {
      setShowConfirmModal(false);
    } else {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancelViewPlanNumbersModal}
      header={showConfirmModal ? 'Cancel changes' : 'Plan number'}
      actions={[
        { title: 'Cancel', kind: 'secondary', autoClose: false, onClick: onCancelViewPlanNumbersModal },
        {
          title: showConfirmModal ? 'Yes' : 'Save',
          kind: 'primary',
          autoClose: false,
          isDisabled: () => (showConfirmModal ? false : !isChanged ? true : !isAllPlanNumberValide),
          onClick: onSave
        }
      ]}
      size="small"
      dataTestid="viewPlanNumbersModal"
    >
      {showConfirmModal ? (
        <div className="px-6 mt-8 mb-16">
          <p className="text-xl font-effra text-gray-70">
            Are you sure you want to cancel without saving your changes?
          </p>
        </div>
      ) : (
        <ViewPlanNumbersModalBody
          planNumbersState={planNumbersState}
          onPlanNumberChange={onPlanNumberChange}
          onDeletePlanNumber={onDeletePlanNumber}
          onAddPlanNumber={onAddPlanNumber}
        />
      )}
    </Modal>
  );
};
